/**
 * RouteError
 *
 * Rendered by React Router (`errorElement`) when a route throws while
 * rendering or loading. Without it React Router shows its raw
 * "Unexpected Application Error!" screen in place of the whole app, so any
 * single bad record takes the entire session down.
 *
 * Mounted on the protected branch so the layout (menu, header) survives and
 * the user can keep navigating, and on the root route as a last resort.
 */

import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { reloadOnceOnStaleChunk } from '@/utils/stale-chunk';

export function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();

  console.error('RouteError caught:', error);

  // A lazy route whose chunk vanished in a deploy: React Router catches the
  // import rejection, so the window-level handler in main.tsx never sees it.
  // Reload once to pick up the new index.html instead of showing an error.
  if (reloadOnceOnStaleChunk(error)) {
    return null;
  }

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : 'Error desconocido';

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <CardTitle>No pudimos mostrar esta sección</CardTitle>
          </div>
          <CardDescription>
            Ocurrió un error al cargar el contenido. El resto del sistema sigue disponible.
          </CardDescription>
        </CardHeader>

        {import.meta.env.DEV && (
          <CardContent>
            <pre className="overflow-auto rounded bg-muted p-2 text-xs">{message}</pre>
          </CardContent>
        )}

        <CardFooter className="flex flex-col sm:flex-row gap-2">
          <Button onClick={() => window.location.reload()} className="w-full sm:w-auto">
            <RefreshCw className="mr-2 h-4 w-4" />
            Reintentar
          </Button>
          <Button onClick={() => navigate('/home')} variant="outline" className="w-full sm:w-auto">
            <Home className="mr-2 h-4 w-4" />
            Ir al inicio
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
